/**
 * Chess Notation System
 * 
 * Handles conversion between moves and algebraic notation, FEN serialization,
 * and PGN export/import.
 * Implements requirements 20.1, 20.2, 20.3, 20.4, 22.1, 22.4 from the chess-game specification.
 */

import {
  Move,
  Position,
  ChessPiece,
  Board,
  PlayerColor,
  CastlingRights,
  BoardState,
  GameMetadata,
  GameSession,
  PieceType,
  positionToAlgebraic,
  algebraicToPosition
} from './types';

/**
 * ChessNotation class for handling chess notation conversions
 */
export class ChessNotation {
  /**
   * Converts a move to standard algebraic notation
   * Requirement 20.1: Record moves in standard algebraic notation
   * 
   * @param move The move to convert
   * @param board The current board state
   * @returns Algebraic notation string (e.g., "e4", "Nf3", "O-O")
   */
  public moveToAlgebraic(move: Move, board: Board): string {
    // Handle castling
    if (move.isCastling) {
      // Kingside castling
      if (move.to.file === 6) {
        return 'O-O';
      }
      // Queenside castling
      return 'O-O-O';
    }

    let notation = '';

    // Add piece symbol (except for pawns)
    if (move.piece.type !== 'pawn') {
      notation += this.getPieceSymbol(move.piece.type);
      
      // Check for disambiguation (when multiple pieces of same type can move to same square)
      if (move.piece.type !== 'king') {
        const disambiguation = this.getDisambiguation(move, board);
        notation += disambiguation;
      }
    }

    // For pawn captures, add the file letter
    if (move.piece.type === 'pawn' && (move.captured || move.isEnPassant)) {
      notation += String.fromCharCode(97 + move.from.file); // 'a' + file
    }

    // Add capture symbol
    if (move.captured || move.isEnPassant) {
      notation += 'x';
    }

    // Add destination square
    notation += positionToAlgebraic(move.to);

    // Add promotion
    if (move.promotion) {
      notation += '=' + this.getPieceSymbol(move.promotion);
    }

    // Add check or checkmate symbol
    if (move.isCheckmate) {
      notation += '#';
    } else if (move.isCheck) {
      notation += '+';
    }

    // Add en passant indicator (optional, for clarity)
    if (move.isEnPassant) {
      notation += ' e.p.';
    }

    return notation;
  }

  /**
   * Parses algebraic notation and converts it to a Move object
   * Requirement 20.2: Parse algebraic notation
   * 
   * @param notation Algebraic notation string
   * @param board Current board state
   * @returns Move object or null if invalid
   */
  public algebraicToMove(notation: string, board: Board): Move | null {
    // Remove check/checkmate symbols and whitespace
    const cleanNotation = notation.trim().replace(/[+#]$/, '').replace(/\s*e\.p\.$/, '');

    // Handle castling
    if (cleanNotation === 'O-O' || cleanNotation === '0-0') {
      return this.parseCastling('kingside', board);
    }
    if (cleanNotation === 'O-O-O' || cleanNotation === '0-0-0') {
      return this.parseCastling('queenside', board);
    }

    // Parse the notation
    let index = 0;
    let pieceType: PieceType = 'pawn';
    let fromFile: number | undefined;
    let fromRank: number | undefined;
    let isCapture = false;
    let promotion: PieceType | undefined;

    // Check for piece type
    const firstChar = cleanNotation[0];
    if (['K', 'Q', 'R', 'B', 'N'].includes(firstChar)) {
      pieceType = this.parsePieceSymbol(firstChar);
      index++;
    }

    // Parse disambiguation and capture
    while (index < cleanNotation.length) {
      const char = cleanNotation[index];
      
      if (char === 'x') {
        isCapture = true;
        index++;
        break;
      }
      
      // Check if this is a file letter (a-h)
      if (char >= 'a' && char <= 'h') {
        // Look ahead to see if this is disambiguation or destination
        const nextIndex = index + 1;
        if (nextIndex < cleanNotation.length) {
          const nextChar = cleanNotation[nextIndex];
          // If next is a digit and there's more after, this is disambiguation
          if (nextChar >= '1' && nextChar <= '8') {
            const afterNext = cleanNotation[nextIndex + 1];
            if (afterNext && afterNext !== '=' && afterNext !== '+' && afterNext !== '#') {
              // This is disambiguation, not destination
              fromFile = char.charCodeAt(0) - 97;
              index++;
              continue;
            }
          } else if (nextChar === 'x') {
            // File before capture is disambiguation
            fromFile = char.charCodeAt(0) - 97;
            index++;
            continue;
          }
        }
        // This is the destination file
        break;
      }
      // Check if this is a rank number (1-8)
      else if (char >= '1' && char <= '8') {
        const nextChar = cleanNotation[index + 1];
        if (!nextChar || nextChar === '=' || nextChar === '+' || nextChar === '#') {
          // This is the destination rank
          break;
        }
        fromRank = parseInt(char) - 1;
        index++;
      } else {
        break;
      }
    }

    // Parse destination square
    if (index + 1 >= cleanNotation.length) {
      return null;
    }

    const destFile = cleanNotation[index].charCodeAt(0) - 97;
    const destRank = parseInt(cleanNotation[index + 1]) - 1;
    
    if (destFile < 0 || destFile > 7 || destRank < 0 || destRank > 7) {
      return null;
    }

    const to: Position = { file: destFile, rank: destRank };
    index += 2;

    // Parse promotion
    if (index < cleanNotation.length && cleanNotation[index] === '=') {
      index++;
      if (index < cleanNotation.length) {
        promotion = this.parsePieceSymbol(cleanNotation[index]);
      }
    }

    // Find the piece that can make this move
    const from = this.findPieceForMove(pieceType, to, board, fromFile, fromRank);
    if (!from) {
      return null;
    }

    const piece = board[from.rank]?.[from.file];
    if (!piece) {
      return null;
    }

    // Create the move object
    const captured = board[to.rank]?.[to.file];
    
    return {
      from,
      to,
      piece,
      captured: captured || undefined,
      promotion,
      isCheck: notation.includes('+'),
      isCheckmate: notation.includes('#'),
      isCastling: false,
      isEnPassant: notation.includes('e.p.'),
      notation,
      timestamp: Date.now()
    };
  }

  /**
   * Converts board state to FEN (Forsyth-Edwards Notation)
   * Requirement 22.1: Save game state
   * 
   * @param board Current board state
   * @param currentPlayer Current player to move
   * @param castlingRights Castling availability
   * @param enPassantSquare En passant target square
   * @param halfMoveClock Half-move clock for fifty-move rule
   * @param fullMoveNumber Full move number
   * @returns FEN string
   */
  public boardToFEN(
    board: Board,
    currentPlayer: PlayerColor,
    castlingRights: CastlingRights,
    enPassantSquare?: Position,
    halfMoveClock: number = 0,
    fullMoveNumber: number = 1
  ): string {
    const parts: string[] = [];

    // 1. Piece placement (from rank 8 to rank 1)
    const rows: string[] = [];
    for (let rank = 7; rank >= 0; rank--) {
      let emptyCount = 0;
      let row = '';
      
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        
        if (piece === null) {
          emptyCount++;
        } else {
          if (emptyCount > 0) {
            row += emptyCount.toString();
            emptyCount = 0;
          }
          row += this.pieceToFENChar(piece);
        }
      }
      
      if (emptyCount > 0) {
        row += emptyCount.toString();
      }
      
      rows.push(row);
    }
    parts.push(rows.join('/'));

    // 2. Active color
    parts.push(currentPlayer === 'white' ? 'w' : 'b');

    // 3. Castling availability
    let castling = '';
    if (castlingRights.whiteKingside) castling += 'K';
    if (castlingRights.whiteQueenside) castling += 'Q';
    if (castlingRights.blackKingside) castling += 'k';
    if (castlingRights.blackQueenside) castling += 'q';
    parts.push(castling || '-');

    // 4. En passant target square
    parts.push(enPassantSquare ? positionToAlgebraic(enPassantSquare) : '-');

    // 5. Halfmove clock
    parts.push(halfMoveClock.toString());

    // 6. Fullmove number
    parts.push(fullMoveNumber.toString());

    return parts.join(' ');
  }

  /**
   * Parses FEN notation and converts it to board state
   * Requirement 22.4: Resume games from exact position
   * 
   * @param fen FEN string
   * @returns BoardState object or null if invalid
   */
  public fenToBoard(fen: string): BoardState | null {
    const parts = fen.trim().split(/\s+/);
    
    if (parts.length !== 6) {
      return null;
    }

    // 1. Parse piece placement
    const board: Board = Array(8).fill(null).map(() => Array(8).fill(null));
    const rows = parts[0].split('/');
    
    if (rows.length !== 8) {
      return null;
    }

    for (let i = 0; i < 8; i++) {
      const rank = 7 - i; // FEN starts from rank 8
      const row = rows[i];
      let file = 0;
      
      for (const char of row) {
        if (char >= '1' && char <= '8') {
          // Empty squares
          file += parseInt(char);
        } else {
          // Piece
          const piece = this.fenCharToPiece(char, { file, rank });
          if (!piece) {
            return null;
          }
          board[rank][file] = piece;
          file++;
        }
      }
    }

    // 2. Parse active color
    const currentPlayer: PlayerColor = parts[1] === 'w' ? 'white' : 'black';

    // 3. Parse castling rights
    const castlingRights: CastlingRights = {
      whiteKingside: parts[2].includes('K'),
      whiteQueenside: parts[2].includes('Q'),
      blackKingside: parts[2].includes('k'),
      blackQueenside: parts[2].includes('q')
    };

    // 4. Parse en passant square
    const enPassantSquare = parts[3] === '-' ? undefined : algebraicToPosition(parts[3]) || undefined;

    // 5. Parse halfmove clock
    const halfMoveClock = parseInt(parts[4]);

    // 6. Parse fullmove number
    const fullMoveNumber = parseInt(parts[5]);

    return {
      board,
      currentPlayer,
      castlingRights,
      enPassantSquare,
      halfMoveClock,
      fullMoveNumber
    };
  }

  /**
   * Exports a game to PGN (Portable Game Notation) format
   * Requirement 20.6: Export move history in PGN format
   * 
   * @param game Game session with metadata
   * @param moves Array of moves
   * @returns PGN string
   */
  public gameToPGN(game: GameSession, moves: Move[]): string {
    const lines: string[] = [];

    // Add metadata tags
    lines.push(`[Event "${game.settings.mode}"]`);
    lines.push(`[Site "PlayHub Games"]`);
    lines.push(`[Date "${new Date(game.startTime).toISOString().split('T')[0].replace(/-/g, '.')}"]`);
    lines.push(`[Round "-"]`);
    lines.push(`[White "${game.players.white.name}"]`);
    lines.push(`[Black "${game.players.black.name}"]`);
    
    // Determine result
    let result = '*'; // Game in progress
    const finalStatus = game.currentState.gameStatus;
    if (finalStatus.type === 'checkmate') {
      result = finalStatus.winner === 'white' ? '1-0' : '0-1';
    } else if (finalStatus.type === 'stalemate' || finalStatus.type === 'draw') {
      result = '1/2-1/2';
    } else if (finalStatus.type === 'resignation') {
      result = finalStatus.winner === 'white' ? '1-0' : '0-1';
    } else if (finalStatus.type === 'timeout') {
      result = finalStatus.winner === 'white' ? '1-0' : '0-1';
    }
    lines.push(`[Result "${result}"]`);

    // Add time control if available
    if (game.settings.timeControl.type !== 'unlimited') {
      const tc = game.settings.timeControl;
      lines.push(`[TimeControl "${tc.initialTime}+${tc.increment}"]`);
    }

    lines.push('');

    // Add moves
    let moveText = '';
    for (let i = 0; i < moves.length; i++) {
      if (i % 2 === 0) {
        // White's move
        const moveNumber = Math.floor(i / 2) + 1;
        moveText += `${moveNumber}. ${moves[i].notation} `;
      } else {
        // Black's move
        moveText += `${moves[i].notation} `;
      }
    }
    
    // Add result at the end
    moveText += result;
    
    // Wrap lines at 80 characters
    const wrappedMoves = this.wrapText(moveText, 80);
    lines.push(wrappedMoves);

    return lines.join('\n');
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Gets the standard piece symbol for algebraic notation
   */
  private getPieceSymbol(type: PieceType): string {
    const symbols: Record<PieceType, string> = {
      king: 'K',
      queen: 'Q',
      rook: 'R',
      bishop: 'B',
      knight: 'N',
      pawn: ''
    };
    return symbols[type];
  }

  /**
   * Parses a piece symbol from algebraic notation
   */
  private parsePieceSymbol(symbol: string): PieceType {
    const pieces: Record<string, PieceType> = {
      'K': 'king',
      'Q': 'queen',
      'R': 'rook',
      'B': 'bishop',
      'N': 'knight'
    };
    return pieces[symbol] || 'pawn';
  }

  /**
   * Determines disambiguation needed when multiple pieces can move to same square
   */
  private getDisambiguation(move: Move, board: Board): string {
    // Find all pieces of the same type and color that can move to the destination
    const candidates: Position[] = [];
    
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && 
            piece.type === move.piece.type && 
            piece.color === move.piece.color &&
            (file !== move.from.file || rank !== move.from.rank)) {
          // This is a different piece of the same type
          // In a real implementation, we would check if it can legally move to the destination
          // For now, we'll just check if any other piece of same type exists
          candidates.push({ file, rank });
        }
      }
    }

    if (candidates.length === 0) {
      return '';
    }

    // Check if file disambiguation is sufficient
    const sameFile = candidates.some(pos => pos.file === move.from.file);
    const sameRank = candidates.some(pos => pos.rank === move.from.rank);

    if (!sameFile) {
      return String.fromCharCode(97 + move.from.file);
    }
    if (!sameRank) {
      return (move.from.rank + 1).toString();
    }
    
    // Need both file and rank
    return positionToAlgebraic(move.from);
  }

  /**
   * Finds the piece that can make a given move
   */
  private findPieceForMove(
    pieceType: PieceType,
    to: Position,
    board: Board,
    fromFile?: number,
    fromRank?: number
  ): Position | null {
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        
        if (!piece || piece.type !== pieceType) {
          continue;
        }

        // Check disambiguation constraints
        if (fromFile !== undefined && file !== fromFile) {
          continue;
        }
        if (fromRank !== undefined && rank !== fromRank) {
          continue;
        }

        // This is a candidate piece
        return { file, rank };
      }
    }

    return null;
  }

  /**
   * Parses castling notation
   */
  private parseCastling(side: 'kingside' | 'queenside', board: Board): Move | null {
    // Find the king (simplified - assumes current player's king)
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === 'king') {
          const toFile = side === 'kingside' ? 6 : 2;
          return {
            from: { file, rank },
            to: { file: toFile, rank },
            piece,
            isCheck: false,
            isCheckmate: false,
            isCastling: true,
            isEnPassant: false,
            notation: side === 'kingside' ? 'O-O' : 'O-O-O',
            timestamp: Date.now()
          };
        }
      }
    }
    return null;
  }

  /**
   * Converts a piece to FEN character
   */
  private pieceToFENChar(piece: ChessPiece): string {
    const chars: Record<PieceType, string> = {
      king: 'k',
      queen: 'q',
      rook: 'r',
      bishop: 'b',
      knight: 'n',
      pawn: 'p'
    };
    
    const char = chars[piece.type];
    return piece.color === 'white' ? char.toUpperCase() : char;
  }

  /**
   * Converts FEN character to piece
   */
  private fenCharToPiece(char: string, position: Position): ChessPiece | null {
    const types: Record<string, PieceType> = {
      'k': 'king',
      'q': 'queen',
      'r': 'rook',
      'b': 'bishop',
      'n': 'knight',
      'p': 'pawn'
    };

    const lowerChar = char.toLowerCase();
    const type = types[lowerChar];
    
    if (!type) {
      return null;
    }

    return {
      type,
      color: char === lowerChar ? 'black' : 'white',
      position,
      hasMoved: false,
      moveCount: 0
    };
  }

  /**
   * Wraps text at specified width
   */
  private wrapText(text: string, width: number): string {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if (currentLine.length + word.length + 1 <= width) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.join('\n');
  }
}
